import { fetchPageAssets } from '../../ducks/hostedAssets.duck';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { storableError } from '../../util/errors';
import { getImageVariantInfo } from '../EditListingPage/EditListingPage.duck';
import { RULE_BASED_COLLECTIONS } from './ruleBasedCollections';

export const ASSET_NAME = 'landing-page';
export const RECOMMENDATIONS_ASSET_NAME = 'recommendations';
export const featuredSectionId = 'featured-listings';
export const MIN_THEMED_LISTINGS = 5; // Minimum listings to show a themed collection

// ================ Action types ================ //
const SEARCH_FEATURED_LISTINGS_REQUEST = 'app/LandingPage/SEARCH_FEATURED_LISTINGS_REQUEST';
const SEARCH_FEATURED_LISTINGS_SUCCESS = 'app/LandingPage/SEARCH_FEATURED_LISTINGS_SUCCESS';
const SEARCH_FEATURED_LISTINGS_ERROR = 'app/LandingPage/SEARCH_FEATURED_LISTINGS_ERROR';

const SEARCH_THEMED_COLLECTIONS_REQUEST = 'app/LandingPage/SEARCH_THEMED_COLLECTIONS_REQUEST';
const SEARCH_THEMED_COLLECTIONS_SUCCESS = 'app/LandingPage/SEARCH_THEMED_COLLECTIONS_SUCCESS';
const SEARCH_THEMED_COLLECTIONS_ERROR = 'app/LandingPage/SEARCH_THEMED_COLLECTIONS_ERROR';

const SEARCH_RULE_BASED_COLLECTIONS_REQUEST = 'app/LandingPage/SEARCH_RULE_BASED_COLLECTIONS_REQUEST';
const SEARCH_RULE_BASED_COLLECTIONS_SUCCESS = 'app/LandingPage/SEARCH_RULE_BASED_COLLECTIONS_SUCCESS';
const SEARCH_RULE_BASED_COLLECTIONS_ERROR = 'app/LandingPage/SEARCH_RULE_BASED_COLLECTIONS_ERROR';

// ================ Reducer ================ //

const initialState = {
  featuredListingIds: [],
  featuredListingsInProgress: false,
  featuredListingsError: null,
  themedCollections: [], // Array of { sectionId, title, listingIds }
  themedCollectionsInProgress: false,
  themedCollectionsError: null,
  ruleBasedCollections: [], // Array of { sectionId, title, listingIds } - auto-populated by query
  ruleBasedCollectionsInProgress: false,
  ruleBasedCollectionsError: null,
};

const resultIds = data => data.data.map(l => l.id);

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SEARCH_FEATURED_LISTINGS_REQUEST:
      return {
        ...state,
        featuredListingsInProgress: true,
      };

    case SEARCH_FEATURED_LISTINGS_SUCCESS:
      return {
        ...state,
        featuredListingsInProgress: false,
        featuredListingIds: resultIds(payload.data),
      };

    case SEARCH_FEATURED_LISTINGS_ERROR:
      return { ...state, featuredListingsInProgress: false, featuredListingsError: payload };

    case SEARCH_THEMED_COLLECTIONS_REQUEST:
      return {
        ...state,
        themedCollectionsInProgress: true,
      };

    case SEARCH_THEMED_COLLECTIONS_SUCCESS:
      return {
        ...state,
        themedCollectionsInProgress: false,
        themedCollections: payload.collections,
      };

    case SEARCH_THEMED_COLLECTIONS_ERROR:
      return {
        ...state,
        themedCollectionsInProgress: false,
        themedCollectionsError: payload,
      };

    case SEARCH_RULE_BASED_COLLECTIONS_REQUEST:
      return {
        ...state,
        ruleBasedCollectionsInProgress: true,
      };

    case SEARCH_RULE_BASED_COLLECTIONS_SUCCESS:
      return {
        ...state,
        ruleBasedCollectionsInProgress: false,
        ruleBasedCollections: payload.collections,
      };

    case SEARCH_RULE_BASED_COLLECTIONS_ERROR:
      return {
        ...state,
        ruleBasedCollectionsInProgress: false,
        ruleBasedCollectionsError: payload,
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const searchFeaturedListingsRequest = () => ({
  type: SEARCH_FEATURED_LISTINGS_REQUEST,
});

export const searchFeaturedListingsSuccess = response => ({
  type: SEARCH_FEATURED_LISTINGS_SUCCESS,
  payload: { data: response.data },
});

export const searchFeaturedListingsError = e => ({
  type: SEARCH_FEATURED_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const searchThemedCollectionsRequest = () => ({
  type: SEARCH_THEMED_COLLECTIONS_REQUEST,
});

export const searchThemedCollectionsSuccess = collections => ({
  type: SEARCH_THEMED_COLLECTIONS_SUCCESS,
  payload: { collections },
});

export const searchThemedCollectionsError = e => ({
  type: SEARCH_THEMED_COLLECTIONS_ERROR,
  error: true,
  payload: e,
});

export const searchRuleBasedCollectionsRequest = () => ({
  type: SEARCH_RULE_BASED_COLLECTIONS_REQUEST,
});

export const searchRuleBasedCollectionsSuccess = collections => ({
  type: SEARCH_RULE_BASED_COLLECTIONS_SUCCESS,
  payload: { collections },
});

export const searchRuleBasedCollectionsError = e => ({
  type: SEARCH_RULE_BASED_COLLECTIONS_ERROR,
  error: true,
  payload: e,
});

// ================ Helper functions ================ //

/**
 * Extract listing ID from a URL or return the ID if it's already a UUID
 * Handles formats:
 * - Full URL: https://shimmybooks.co.uk/l/book-title/6908b09c-0ddf-4c7b-9815-680d99b69ebc
 * - Relative URL: /l/book-title/6908b09c-0ddf-4c7b-9815-680d99b69ebc
 * - Raw UUID: 6908b09c-0ddf-4c7b-9815-680d99b69ebc
 */
const extractListingId = urlOrId => {
  if (!urlOrId || typeof urlOrId !== 'string') {
    return null;
  }

  const trimmed = urlOrId.trim();
  
  // UUID regex pattern
  const uuidPattern = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
  const match = trimmed.match(uuidPattern);
  
  return match ? match[0] : null;
};

/**
 * Parse listing IDs from a text field that may contain:
 * - Comma-separated values
 * - Newline-separated values
 * - Mix of URLs and raw UUIDs
 */
const parseListingIds = text => {
  if (!text || typeof text !== 'string') {
    return [];
  }

  // Split by comma or newline
  const items = text.split(/[,\n]+/);
  
  // Extract IDs and filter out nulls
  const ids = items
    .map(item => extractListingId(item))
    .filter(id => id !== null);
  
  // Remove duplicates
  return [...new Set(ids)];
};

/**
 * Extract section data from recommendations page
 * Only include sections that:
 * - Have sectionName starting with 'active-'
 * - Have at least MIN_THEMED_LISTINGS valid listing IDs
 */
const extractThemedCollections = (pageData, minListings = MIN_THEMED_LISTINGS) => {
  if (!pageData?.sections) {
    return [];
  }

  const collections = pageData.sections
    .filter(section => {
      // Only include sections with sectionName starting with 'active-'
      const sectionIdentifier = section.sectionName || section.sectionId;
      return sectionIdentifier && sectionIdentifier.startsWith('active-');
    })
    .map(section => {
      // Use sectionName as the identifier (fallback to sectionId for backwards compatibility)
      const sectionIdentifier = section.sectionName || section.sectionId || 'unnamed';
      
      // Extract title from section title field
      const title = section.title?.content || sectionIdentifier;
      
      // Extract listing IDs from description field
      const descriptionText = section.description?.content || '';
      const listingIds = parseListingIds(descriptionText);

      return {
        sectionId: sectionIdentifier,
        title,
        listingIds,
      };
    })
    .filter(collection => {
      // Only include collections with enough listings
      return collection.listingIds.length >= minListings;
    });

  return collections;
};

// Listing cards show the seller's photo; these are its small square sizes.
const SELLER_AVATAR_VARIANTS = ['variants.square-small', 'variants.square-small2x'];

// ================ Thunks ================ //

export const fetchFeaturedListings = config => async (dispatch, getState, sdk) => {
  try {
    dispatch(searchFeaturedListingsRequest());
    const imageVariantInfo = getImageVariantInfo(config.layout.listingImage);

    const response = await sdk.listings.query({
      include: ['author', 'author.profileImage', 'images', 'currentStock'],
      'fields.image': [...imageVariantInfo.fieldsImage, ...SELLER_AVATAR_VARIANTS],
      ...imageVariantInfo.imageVariants,
      'limit.images': 1,
      perPage: 15,
    });

    const listingFields = config?.listing?.listingFields;
    const sanitizeConfig = { listingFields };

    dispatch(addMarketplaceEntities(response, sanitizeConfig));
    dispatch(searchFeaturedListingsSuccess(response));
    return response;
  } catch (error) {
    return dispatch(searchFeaturedListingsError(storableError(error)));
  }
};

/**
 * Fetch themed collections from recommendations page asset
 * This will:
 * 1. Get recommendations page data from pageAssetsData
 * 2. Extract collections with 'active-' prefix
 * 3. Query listings by ID
 * 4. Store valid collections that meet minimum threshold
 */
export const fetchThemedCollections = (config, pageAssetsData) => async (
  dispatch,
  getState,
  sdk
) => {
  try {
    dispatch(searchThemedCollectionsRequest());

    // Get recommendations page data
    const recommendationsData = pageAssetsData?.recommendations?.data;
    
    if (!recommendationsData) {
      // No recommendations page configured, that's okay
      dispatch(searchThemedCollectionsSuccess([]));
      return [];
    }

    // Extract active collections with minimum listings
    const collections = extractThemedCollections(recommendationsData);

    if (collections.length === 0) {
      dispatch(searchThemedCollectionsSuccess([]));
      return [];
    }

    // Fetch all listings for all collections
    // We'll query each collection separately to maintain collection structure
    const imageVariantInfo = getImageVariantInfo(config.layout.listingImage);
    const listingFields = config?.listing?.listingFields;
    const sanitizeConfig = { listingFields };

    const collectionsWithListings = await Promise.all(
      collections.map(async collection => {
        try {
          // Query listings by IDs
          const response = await sdk.listings.query({
            ids: collection.listingIds,
            include: ['author', 'author.profileImage', 'images', 'currentStock'],
            'fields.image': [...imageVariantInfo.fieldsImage, ...SELLER_AVATAR_VARIANTS],
            ...imageVariantInfo.imageVariants,
            'limit.images': 1,
            perPage: 100, // Max listings per collection
          });

          // Add entities to Redux store
          dispatch(addMarketplaceEntities(response, sanitizeConfig));

          // Get the actual listing IDs that were returned (filters out sold/deleted)
          const validListingIds = response.data.data.map(l => l.id);

          return {
            ...collection,
            listingIds: validListingIds,
            listingCount: validListingIds.length,
          };
        } catch (error) {
          console.error(`Error fetching listings for collection ${collection.sectionId}:`, error);
          return {
            ...collection,
            listingIds: [],
            listingCount: 0,
          };
        }
      })
    );

    // Filter out collections that don't meet minimum threshold after fetching
    const validCollections = collectionsWithListings.filter(
      collection => collection.listingCount >= MIN_THEMED_LISTINGS
    );

    dispatch(searchThemedCollectionsSuccess(validCollections));
    return validCollections;
  } catch (error) {
    console.error('Error fetching themed collections:', error);
    dispatch(searchThemedCollectionsError(storableError(error)));
    return [];
  }
};

/**
 * Fetch rule-based collections declared in ruleBasedCollections.js.
 *
 * Each declared collection runs a single listings query (in parallel), then:
 * 1. Optionally refines the results with the collection's `matches` predicate
 * 2. Registers the listings in the Redux store
 * 3. Keeps the collection only if it meets MIN_THEMED_LISTINGS
 *
 * This mirrors fetchThemedCollections so both kinds of carousel render
 * identically and follow the same "hide if too few listings" rule. It does not
 * depend on any page asset, so it can run as soon as config is available.
 */
export const fetchRuleBasedCollections = config => async (dispatch, getState, sdk) => {
  try {
    dispatch(searchRuleBasedCollectionsRequest());

    if (RULE_BASED_COLLECTIONS.length === 0) {
      dispatch(searchRuleBasedCollectionsSuccess([]));
      return [];
    }

    const imageVariantInfo = getImageVariantInfo(config.layout.listingImage);
    const listingFields = config?.listing?.listingFields;
    const sanitizeConfig = { listingFields };

    const collectionsWithListings = await Promise.all(
      RULE_BASED_COLLECTIONS.map(async ({ sectionId, title, queryParams, matches }) => {
        try {
          const response = await sdk.listings.query({
            ...queryParams,
            include: ['author', 'author.profileImage', 'images', 'currentStock'],
            'fields.image': [...imageVariantInfo.fieldsImage, ...SELLER_AVATAR_VARIANTS],
            ...imageVariantInfo.imageVariants,
            'limit.images': 1,
            perPage: 100,
          });

          // Add entities to Redux store
          dispatch(addMarketplaceEntities(response, sanitizeConfig));

          // Refine the server results with the optional client-side predicate
          const returnedListings = response.data.data;
          const matched =
            typeof matches === 'function' ? returnedListings.filter(matches) : returnedListings;
          const listingIds = matched.map(l => l.id);

          return {
            sectionId,
            title,
            listingIds,
            listingCount: listingIds.length,
          };
        } catch (error) {
          console.error(`Error fetching rule-based collection ${sectionId}:`, error);
          return {
            sectionId,
            title,
            listingIds: [],
            listingCount: 0,
          };
        }
      })
    );

    // Follow the same threshold rule as the manually curated collections
    const validCollections = collectionsWithListings.filter(
      collection => collection.listingCount >= MIN_THEMED_LISTINGS
    );

    dispatch(searchRuleBasedCollectionsSuccess(validCollections));
    return validCollections;
  } catch (error) {
    console.error('Error fetching rule-based collections:', error);
    dispatch(searchRuleBasedCollectionsError(storableError(error)));
    return [];
  }
};

export const loadData = (params, search) => async dispatch => {
  // Always fetch landing page - this is required
  const landingPageAsset = {
    landingPage: `content/pages/${ASSET_NAME}.json`,
  };
  
  // Fetch landing page first
  const landingPageResult = await dispatch(fetchPageAssets(landingPageAsset, true));
  
  // Try to fetch recommendations, but don't fail if it doesn't exist
  // This allows the feature to be optional - if the page exists, great!
  // If not, the themed collections will simply be empty (handled in fetchThemedCollections)
  try {
    const recommendationsAsset = {
      recommendations: `content/pages/${RECOMMENDATIONS_ASSET_NAME}.json`,
    };
    await dispatch(fetchPageAssets(recommendationsAsset, false));
  } catch (e) {
    // Recommendations page doesn't exist - that's okay, themed collections will be empty
    // The graceful handling in fetchThemedCollections will handle this
  }
  
  return landingPageResult;
};
